import React, {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/** localStorage key for the cached application version. */
const VERSION_STORAGE_KEY = "app-version";

/** Polling interval in milliseconds (5 minutes). */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Context value exposed by the {@link VersionPollingProvider}.
 */
export interface VersionPollingContextValue {
  /** Whether a new deployment has been detected. */
  isUpdateAvailable: boolean;
  /** The newly detected version string, or null if no update. */
  newVersion: string | null;
  /** The currently running version (stored in localStorage on first load). */
  currentVersion: string | null;
}

const defaultContextValue: VersionPollingContextValue = {
  isUpdateAvailable: false,
  newVersion: null,
  currentVersion: null,
};

export const VersionPollingContext =
  createContext<VersionPollingContextValue>(defaultContextValue);

/**
 * Reports non-fatal errors to the shared browser error reporter when available.
 */
const reportError = (error: unknown, context: string): void => {
  if (typeof window === "undefined") {
    return;
  }
  const errorReporting = (
    window as Window & {
      errorReporting?: {
        captureException: (exception: unknown, metadata?: unknown) => void;
      };
    }
  ).errorReporting;
  errorReporting?.captureException(error, { context });
};

/**
 * Reads a string value from localStorage.
 */
const readStoredVersion = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return localStorage.getItem(VERSION_STORAGE_KEY);
  } catch (error) {
    reportError(error, "readStoredVersion");
    return null;
  }
};

/**
 * Persists a version string in localStorage.
 */
const writeStoredVersion = (version: string): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(VERSION_STORAGE_KEY, version);
  } catch (error) {
    reportError(error, "writeStoredVersion");
  }
};

/**
 * Fetches the deployed version from /version.json with a cache-busting
 * query parameter. Returns the version string, or null on failure.
 */
const fetchDeployedVersion = async (): Promise<string | null> => {
  try {
    const url = `/version.json?t=${Date.now()}`;
    const response = await fetch(url);

    if (!response.ok) {
      return null;
    }

    const data: { version?: string } = await response.json();

    return data.version ?? null;
  } catch (error) {
    reportError(error, "fetchDeployedVersion");
    return null;
  }
};

/**
 * Polls a static `/version.json` served by nginx to detect new SPA
 * deployments. On first mount, stores the current version in localStorage
 * silently. Every 5 minutes, compares the fetched version against the
 * stored value and sets `isUpdateAvailable` when a mismatch is detected.
 */
export const VersionPollingProvider = ({ children }: { children: ReactNode }) => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Periodic poll — compares the fetched version against localStorage.
   * Only flags an update; never writes to localStorage (the mount
   * initialization handles that after a refresh).
   */
  const pollVersion = useCallback(async () => {
    const deployedVersion = await fetchDeployedVersion();

    if (!deployedVersion) {
      return;
    }

    const storedVersion = readStoredVersion();

    if (!storedVersion) {
      // Edge case — storage was cleared between polls; re-seed silently
      writeStoredVersion(deployedVersion);
      setCurrentVersion(deployedVersion);
      return;
    }

    if (deployedVersion !== storedVersion) {
      setIsUpdateAvailable(true);
      setNewVersion(deployedVersion);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    /**
     * Mount initialization: fetch the deployed version and reconcile
     * localStorage. If the stored version differs (or doesn't exist),
     * update silently — this handles both first load and page refresh
     * after a deploy. The dialog only fires from periodic polls.
     */
    const initializeVersion = async () => {
      const deployedVersion = await fetchDeployedVersion();

      if (!deployedVersion) {
        return;
      }

      const storedVersion = readStoredVersion();

      if (!storedVersion || storedVersion !== deployedVersion) {
        // First load ever, or user refreshed to a newer deploy —
        // silently sync localStorage to the current version
        writeStoredVersion(deployedVersion);
        setCurrentVersion(deployedVersion);
        setIsUpdateAvailable(false);
        setNewVersion(null);
      } else {
        // Same version — restore state for context consumers
        setCurrentVersion(storedVersion);
      }
    };

    void initializeVersion();

    // Start periodic polling
    intervalRef.current = setInterval(() => {
      void pollVersion();
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [pollVersion]);

  const contextValue = useMemo<VersionPollingContextValue>(
    () => ({
      isUpdateAvailable,
      newVersion,
      currentVersion,
    }),
    [isUpdateAvailable, newVersion, currentVersion]
  );

  return (
    <VersionPollingContext.Provider value={contextValue}>
      {children}
    </VersionPollingContext.Provider>
  );
};
